import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { lighted, lightable, LightningProps } from 'owa-lightning-v2';
import { ControlIcons } from 'owa-control-icons';
import { IconButton, ButtonType } from '@fluentui/react/lib/Button';
import { Link } from '@fluentui/react/lib/Link';
import { logUsage } from 'owa-analytics';
import { useWindowEvent } from 'owa-react-hooks/lib/useWindowEvent';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';

const styles = require('./UpsellBanner.scss');
const classNames = require('classnames/bind').bind(styles);

export interface UpsellBannerProps extends LightningProps {
    lid: string;
    upsellName: string;
    upsellContentToShow: UpsellBannerContent;
    onContentLoadCallback?: () => void;
    onContentLinkCallBack?: () => void;
    onDismissCallback?: () => void;
    onBannerClickCallback?: () => void;
    disableBannerClick?: boolean;
    onBannerUnloadCallback?: () => void;
}

export interface UpsellBannerContent {
    contentId: string;
    contentMessage: string;
    contentLinkHref: string;
    contentLinkButtonText: string;
    isEnUsOnly?: boolean;
    isBannerAggresive?: boolean;
    dismissButtonText?: string;
    onDismissButtonCallback?: () => void;
}

export const UpsellBanner = lightable(
    observer(function UpsellBanner(props: UpsellBannerProps) {
        const memoizedLogData = React.useMemo(() => {
            return {
                upsellId: props.lid,
                upsellName: props.upsellName,
                upsellContentId: props.upsellContentToShow.contentId,
                upsellCulture: getUserConfiguration()?.SessionSettings?.UserCulture || '',
            };
        }, []);

        React.useEffect(() => {
            logUsage('UpsellBannerLoad', memoizedLogData, { isCore: true });
            if (props.onContentLoadCallback) {
                props.onContentLoadCallback();
            }
        }, []);

        const upsellBannerUnloadHandler = React.useCallback(() => {
            logUsage('UpsellBannerUnmount', memoizedLogData, { isCore: true });
            lighted(props.lid);
            if (props.onBannerUnloadCallback) {
                props.onBannerUnloadCallback();
            }
        }, []);

        useWindowEvent('beforeunload', upsellBannerUnloadHandler, []);

        const onDismiss = React.useCallback(() => {
            lighted(props.lid);
            if (props.onDismissCallback) {
                props.onDismissCallback();
            }

            logUsage('UpsellBannerDismiss', memoizedLogData, { isCore: true });
        }, []);

        const onContentLinkClick = React.useCallback((evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            if (props.onContentLinkCallBack) {
                props.onContentLinkCallBack();
            }

            if (props.upsellContentToShow.contentLinkHref) {
                logUsage('UpsellBannerLinkClick', memoizedLogData, { isCore: true });
                window.open(props.upsellContentToShow.contentLinkHref, '_blank');
            }
        }, []);

        const onBannerClick = React.useCallback(() => {
            if (!props.disableBannerClick) {
                if (props.onBannerClickCallback) {
                    props.onBannerClickCallback();
                }

                if (props.upsellContentToShow.contentLinkHref) {
                    logUsage('UpsellBannerClick', memoizedLogData, { isCore: true });
                    window.open(props.upsellContentToShow.contentLinkHref, '_blank');
                }
            }
        }, []);

        const onDismissButtonClick = React.useCallback(() => {
            if (props.upsellContentToShow.onDismissButtonCallback) {
                props.upsellContentToShow.onDismissButtonCallback();
            }

            logUsage('UpsellBannerDismissButton', memoizedLogData, { isCore: true });
        }, []);
        const bannerStyle = !props.disableBannerClick
            ? classNames(styles.upsellContent, styles.upsellContentCursor)
            : styles.upsellContent;
        return (
            <>
                <div
                    className={classNames(
                        styles.topUpsellBanner,
                        props.upsellContentToShow.isBannerAggresive
                            ? styles.aggressiveBanner
                            : styles.themedBanner
                    )}>
                    <div className={bannerStyle} onClick={onBannerClick}>
                        <div>
                            <span>{props.upsellContentToShow.contentMessage} </span>
                            {props.upsellContentToShow.contentLinkButtonText && (
                                <span className={styles.bannerButton}>
                                    <Link onClick={onContentLinkClick}>
                                        {props.upsellContentToShow.contentLinkButtonText}
                                    </Link>
                                </span>
                            )}
                            {props.upsellContentToShow.dismissButtonText && (
                                <span className={styles.bannerButton}>
                                    <Link onClick={onDismissButtonClick}>
                                        {props.upsellContentToShow.dismissButtonText}
                                    </Link>
                                </span>
                            )}
                        </div>
                    </div>
                    <div>
                        <IconButton
                            buttonType={ButtonType.command}
                            onClick={onDismiss}
                            iconProps={{ iconName: ControlIcons.Cancel }}
                            className={styles.upsellBannerClose}
                        />
                    </div>
                </div>
            </>
        );
    })
);
